// Simple session manager for sliding sessions and idle timeout
// - Detects user activity to reset idle timer
// - Proactively refreshes access token shortly before expiry when user is active
// - Avoids refreshing while idle; lets session naturally expire

import apiService from '../services/apiService';

type NullableNumber = number | null;

interface SessionManagerOptions {
	idleTimeoutMs?: number; // e.g., 30 * 60 * 1000
	refreshSkewSeconds?: number; // refresh N seconds before exp
	checkIntervalMs?: number; // how often to check token expiry while active
}

class SessionManager {
	private idleTimeoutMs: number;
	private refreshSkewSeconds: number;
	private checkIntervalMs: number;
	private idleTimer: any = null;
	private checkTimer: any = null;
	private isIdle = false;
	private started = false;
	private bc: BroadcastChannel | null = null;
	private redirecting = false;
	private mouseMoveHandler: ((e: MouseEvent) => void) | null = null;

	/**
	 * 构造函数，用于初始化 SessionManager 实例
	 * @param options - 可选的配置选项对象
	 * @param options.idleTimeoutMs - 空闲超时时间（毫秒），默认为 30 分钟 (1800000 ms)
	 * @param options.refreshSkewSeconds - 在令牌过期前多少秒刷新令牌，默认为 120 秒 (2 分钟)
	 * @param options.checkIntervalMs - 检查令牌过期状态的时间间隔（毫秒），默认为 30 秒 (30000 ms)
	 */
	constructor(options?: SessionManagerOptions) {
		// 设置空闲超时时间，如果未提供则默认为 30 分钟【用户无操作 30 分钟后视为闲置】
		this.idleTimeoutMs = options?.idleTimeoutMs ?? 60 * 60 * 1000; // 30 min

		// 设置令牌刷新提前时间，如果未提供则默认为 5 分钟【在令牌过期前 5 分钟刷新】
		this.refreshSkewSeconds = options?.refreshSkewSeconds ?? 300; // 5 min

		// 设置检查令牌状态的时间间隔，如果未提供则默认为 30 秒【每 30 秒检查一次令牌状态】
		this.checkIntervalMs = options?.checkIntervalMs ?? 30 * 1000; // 30 sec
	}

	init() {
		if (this.started) return;
		this.started = true;
		this.attachEventListeners();
		this.resetIdleTimer();
		this.startPeriodicCheck();
		try {
			this.bc = new BroadcastChannel('session');
			this.bc.onmessage = (e) => {
				if (e?.data?.type === 'logout') {
					this.performForceLogoutAndRedirect('remote');
				}
			};
		} catch {
			// BroadcastChannel not supported, ignore
		}
	}

	stop() {
		if (!this.started) return;
		this.started = false;
		this.detachEventListeners();
		if (this.idleTimer) clearTimeout(this.idleTimer);
		if (this.checkTimer) clearInterval(this.checkTimer);
		this.idleTimer = null;
		this.checkTimer = null;
		if (this.bc) {
			try { this.bc.close(); } catch {}
			this.bc = null;
		}
	}

	private attachEventListeners() {
		const reset = this.onUserActivity;
		// 僅使用較可靠的「用戶主動操作」事件，避免 Linux 環境抖動導致誤判活躍
		window.addEventListener('keydown', reset, { passive: true });
		window.addEventListener('click', reset, { passive: true });
		window.addEventListener('touchstart', reset, { passive: true });
		window.addEventListener('wheel', reset, { passive: true });
		// 可選：節流 mousemove，避免背景抖動
		let lastMoveTs = 0;
		let lastX = -1;
		let lastY = -1;
		this.mouseMoveHandler = (e: MouseEvent) => {
			const now = Date.now();
			// 每 2 秒判斷一次，且需要有明顯位移
			if (now - lastMoveTs < 2000) return;
			if (typeof e.movementX === 'number' && typeof e.movementY === 'number') {
				if (Math.abs(e.movementX) < 2 && Math.abs(e.movementY) < 2) return;
			}
			if (e.clientX === lastX && e.clientY === lastY) return;
			lastMoveTs = now;
			lastX = e.clientX;
			lastY = e.clientY;
			reset();
		};
		window.addEventListener('mousemove', this.mouseMoveHandler, { passive: true });

		// 移除易造成誤觸發的事件：focus/scroll/visibilitychange
		window.addEventListener('storage', this.onStorageSync);
	}

	private detachEventListeners() {
		const reset = this.onUserActivity;
		window.removeEventListener('keydown', reset as any);
		window.removeEventListener('click', reset as any);
		window.removeEventListener('touchstart', reset as any);
		window.removeEventListener('wheel', reset as any);
		if (this.mouseMoveHandler) {
			window.removeEventListener('mousemove', this.mouseMoveHandler as any);
			this.mouseMoveHandler = null;
		}
		window.removeEventListener('storage', this.onStorageSync as any);
	}

	private onStorageSync = (e: StorageEvent) => {
		if (e.key === 'authToken' && e.newValue === null) {
			this.performForceLogoutAndRedirect('storage');
		}
	};

	private onUserActivity = () => {
		if (this.isIdle) {
			this.isIdle = false;
		}
		this.resetIdleTimer();
	};

	private resetIdleTimer() {
		if (this.idleTimer) clearTimeout(this.idleTimer);
		this.idleTimer = setTimeout(() => {
			this.onIdleTimeout();
		}, this.idleTimeoutMs);
	}

	private onIdleTimeout() {
		this.isIdle = true;
		this.performForceLogoutAndRedirect('idle');
	}

	private startPeriodicCheck() {
		if (this.checkTimer) clearInterval(this.checkTimer);
		this.checkTimer = setInterval(() => {
			this.maybeRefreshToken();
		}, this.checkIntervalMs);
		// Run once immediately
		this.maybeRefreshToken();
	}

	private async maybeRefreshToken() {
		try {
			const token = localStorage.getItem('authToken');
			const refreshToken = localStorage.getItem('refreshToken');
			if (!token || !refreshToken) return;
			if (this.isIdle) return; // do not refresh while idle
			const expMs = this.getTokenExpiryMs(token);
			if (!expMs) return;
			const now = Date.now();
			const skewMs = this.refreshSkewSeconds * 1000;
			if (expMs - now <= skewMs) {
				await apiService.refreshTokens({ refreshToken });
			}
		} catch {
			// Ignore; apiService will handle failures and redirect if needed
		}
	}

	private getTokenExpiryMs(token: string): NullableNumber {
		try {
			const parts = token.split('.');
			if (parts.length !== 3) return null;
			const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
			if (!payload?.exp) return null;
			return payload.exp * 1000;
		} catch {
			return null;
		}
	}

	private performForceLogoutAndRedirect(reason?: string) {
		if (this.redirecting) return;
		this.redirecting = true;
		try {
			localStorage.removeItem('authToken');
			localStorage.removeItem('refreshToken');
			try { this.bc?.postMessage({ type: 'logout', reason }); } catch {}
			const base = ((import.meta as any)?.env?.BASE_URL) || '/';
			const to = (base?.endsWith('/') ? base : `${base}/`) + 'login';
			window.location.href = to;
		} catch {
			// noop
		}
	}
}

const sessionManager = new SessionManager();
export default sessionManager;


